import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Pagination,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";

import {
  Mail,
  MailOpen,
  Inbox,
  Archive,
  CheckCircle,
  RefreshCcw,
  Search,
  Trash2,
  X,
  Reply,
  Phone,
  Clock,
} from "lucide-react";

const API_BASE = "https://api.sftech.ng";

// NOTE: confirm this matches whatever key your main admin login
// actually stores the JWT under.
function getAdminToken() {
  return localStorage.getItem("token");
}

async function apiFetch(path, options = {}) {
  const token = getAdminToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, status: response.status, data };
}

const STATUSES = ["new", "read", "responded", "archived"];

const theme = {
  bg: "#050B14",
  panel: "#071B36",
  glass: "rgba(255,255,255,0.04)",
  stroke: "rgba(0,229,192,0.18)",
  teal: "#00e5c0",
  text: "#E6F1FF",
  red: "#ff6b6b",
  amber: "#ffb020",
  blue: "#5b9dff",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: theme.text,
    bgcolor: theme.glass,
    borderRadius: 2,
    "& fieldset": { borderColor: theme.stroke },
    "&:hover fieldset": { borderColor: theme.teal },
    "&.Mui-focused fieldset": { borderColor: theme.teal },
  },
  "& .MuiInputLabel-root": { color: "#9FB3C8" },
  "& .MuiSelect-icon": { color: "#9FB3C8" },
};

function StatusChip({ value }) {
  const tones = {
    new: { bg: "rgba(91,157,255,0.15)", color: theme.blue },
    read: { bg: "rgba(255,255,255,0.08)", color: "#9FB3C8" },
    responded: { bg: "rgba(0,229,192,0.15)", color: theme.teal },
    archived: { bg: "rgba(255,176,32,0.15)", color: theme.amber },
  };
  const t = tones[value] || tones.read;
  return (
    <Chip
      size="small"
      label={value || "—"}
      sx={{ bgcolor: t.bg, color: t.color, fontWeight: 800, textTransform: "capitalize" }}
    />
  );
}

function timeAgo(dateString) {
  if (!dateString) return "";
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

export default function ContactMessagesPage() {
  const [summary, setSummary] = useState({ total: null, new: null, responded: null, archived: null });
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const [allMsg, newMsg, respondedMsg, archivedMsg] = await Promise.all([
        apiFetch(`/api/contact?limit=1`),
        apiFetch(`/api/contact?limit=1&status=new`),
        apiFetch(`/api/contact?limit=1&status=responded`),
        apiFetch(`/api/contact?limit=1&status=archived`),
      ]);
      setSummary({
        total: allMsg.data?.total ?? 0,
        new: newMsg.data?.total ?? 0,
        responded: respondedMsg.data?.total ?? 0,
        archived: archivedMsg.data?.total ?? 0,
      });
    } catch (err) {
      console.error("Summary load error:", err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({ page, limit });
      if (q.trim()) params.set("q", q.trim());
      if (statusFilter) params.set("status", statusFilter);

      const { ok, data } = await apiFetch(`/api/contact?${params.toString()}`);
      if (!ok || !data.success) {
        setErrorMsg(data.message || "Failed to load messages.");
        return;
      }
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
      setErrorMsg("Network error loading messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadMessages();
  };

  const openMessage = async (msg) => {
    setActionError("");
    setDetailLoading(true);
    setSelected(msg);

    // Fetching the single message auto-marks it "read" server-side if
    // it was still "new" — reflect that in the list + summary right away.
    const { ok, data } = await apiFetch(`/api/contact/${msg.id}`);
    setDetailLoading(false);

    if (ok && data.success) {
      setSelected(data.contact);
      if (msg.status === "new" && data.contact.status !== "new") {
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? data.contact : m)));
        loadSummary();
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/contact/${selected.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: newStatus }),
    });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not update status.");
      return;
    }

    setSelected(data.contact);
    setMessages((prev) => prev.map((m) => (m.id === selected.id ? data.contact : m)));
    loadSummary();
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete this message from ${selected.full_name}? This cannot be undone.`)) return;

    setActionLoading(true);
    setActionError("");

    const { ok, data } = await apiFetch(`/api/contact/${selected.id}`, { method: "DELETE" });

    setActionLoading(false);

    if (!ok || !data.success) {
      setActionError(data.message || "Could not delete message.");
      return;
    }

    setSelected(null);
    loadMessages();
    loadSummary();
  };

  const replyMailto = useMemo(() => {
    if (!selected) return "#";
    const subject = encodeURIComponent(`Re: ${selected.subject || "Your enquiry to SF-TECH"}`);
    return `mailto:${selected.email}?subject=${subject}`;
  }, [selected]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: theme.bg, color: theme.text }}>
      {/* HEADER */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: theme.panel, borderBottom: `1px solid ${theme.stroke}` }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box>
            <Typography fontWeight={800} sx={{ letterSpacing: 0.5 }}>
              Contact Messages
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              Website Enquiry Management
            </Typography>
          </Box>

          <Button
            startIcon={<RefreshCcw size={16} />}
            onClick={() => { loadSummary(); loadMessages(); }}
            sx={{ color: theme.teal, fontWeight: 700, textTransform: "none" }}
          >
            Sync
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* SUMMARY CARDS */}
        <Grid container spacing={2}>
          {[
            { label: "Total Messages", value: summary.total, icon: <Inbox /> },
            { label: "New", value: summary.new, icon: <Mail /> },
            { label: "Responded", value: summary.responded, icon: <CheckCircle /> },
            { label: "Archived", value: summary.archived, icon: <Archive /> },
          ].map((c) => (
            <Grid item xs={12} md={3} key={c.label}>
              <Card
                sx={{
                  bgcolor: theme.glass,
                  border: `1px solid ${theme.stroke}`,
                  backdropFilter: "blur(12px)",
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography sx={{ opacity: 0.7, fontSize: 13 }}>{c.label}</Typography>
                    <Box sx={{ color: theme.teal }}>{c.icon}</Box>
                  </Box>
                  <Typography variant="h4" fontWeight={900} sx={{ mt: 1, color: theme.teal }}>
                    {summaryLoading ? <CircularProgress size={22} sx={{ color: theme.teal }} /> : c.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* SEARCH + FILTER */}
        <Box component="form" onSubmit={handleSearch} sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 4, mb: 3 }}>
          <TextField
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, subject..."
            sx={{ ...fieldSx, minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color="#9FB3C8" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ ...fieldSx, minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              MenuProps={{ PaperProps: { sx: { bgcolor: theme.panel, color: theme.text } } }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            type="submit"
            sx={{ bgcolor: theme.teal, color: "#001b1a", fontWeight: 800, px: 3, "&:hover": { bgcolor: "#00c9a8" } }}
          >
            Search
          </Button>
        </Box>

        {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

        {/* MESSAGE LIST */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: theme.teal }} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ opacity: 0.5, textAlign: "center", py: 6 }}>No messages found.</Typography>
        ) : (
          messages.map((m) => (
            <Box
              key={m.id}
              onClick={() => openMessage(m)}
              sx={{
                p: 2.5,
                mb: 1.5,
                borderRadius: 2,
                bgcolor: theme.glass,
                border: `1px solid ${m.status === "new" ? theme.blue : theme.stroke}`,
                borderLeftWidth: m.status === "new" ? 3 : 1,
                cursor: "pointer",
                transition: "border-color 0.2s, transform 0.15s",
                "&:hover": { borderColor: theme.teal, transform: "translateX(2px)" },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {m.status === "new" ? (
                      <Mail size={14} color={theme.blue} />
                    ) : (
                      <MailOpen size={14} color="#9FB3C8" />
                    )}
                    <Typography fontWeight={m.status === "new" ? 900 : 700}>
                      {m.full_name}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.5 }}>
                      · {m.email}
                    </Typography>
                  </Box>

                  {m.subject && (
                    <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85, fontWeight: 700 }}>
                      {m.subject}
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      opacity: 0.55,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.message}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                  <StatusChip value={m.status} />
                  <Typography variant="caption" sx={{ opacity: 0.4, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Clock size={11} />
                    {timeAgo(m.created_at)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}

        {!loading && messages.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              sx={{
                "& .MuiPaginationItem-root": { color: "#9FB3C8" },
                "& .Mui-selected": { bgcolor: `${theme.teal}33 !important`, color: theme.teal },
              }}
            />
          </Box>
        )}
      </Box>

      {/* MESSAGE DETAIL DIALOG */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: theme.panel, color: theme.text, border: `1px solid ${theme.stroke}`, borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Message Detail
          <IconButton onClick={() => setSelected(null)} sx={{ color: "#9FB3C8" }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: theme.stroke }}>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} sx={{ color: theme.teal }} />
            </Box>
          ) : selected ? (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="h6" fontWeight={900}>{selected.full_name}</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>{selected.email}</Typography>
                  {selected.phone && (
                    <Typography variant="body2" sx={{ opacity: 0.7, display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <Phone size={13} /> {selected.phone}
                    </Typography>
                  )}
                </Box>
                <StatusChip value={selected.status} />
              </Box>

              {selected.subject && (
                <>
                  <Divider sx={{ my: 2, borderColor: theme.stroke }} />
                  <Typography variant="caption" sx={{ opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Subject
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontWeight: 700 }}>{selected.subject}</Typography>
                </>
              )}

              <Divider sx={{ my: 2, borderColor: theme.stroke }} />
              <Typography variant="caption" sx={{ opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Message
              </Typography>
              <Typography sx={{ mt: 0.5, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                {selected.message}
              </Typography>

              <Typography variant="caption" sx={{ display: "block", mt: 3, opacity: 0.4 }}>
                Submitted {new Date(selected.created_at).toLocaleString()}
              </Typography>

              {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}

              <Divider sx={{ my: 2, borderColor: theme.stroke }} />
              <Typography variant="caption" sx={{ opacity: 0.6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Change Status
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {STATUSES.map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    onClick={() => s !== selected.status && !actionLoading && handleStatusChange(s)}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 700,
                      cursor: s === selected.status ? "default" : "pointer",
                      bgcolor: s === selected.status ? theme.teal : "rgba(255,255,255,0.08)",
                      color: s === selected.status ? "#001b1a" : "#9FB3C8",
                      opacity: actionLoading ? 0.5 : 1,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : null}
        </DialogContent>

        {selected && !detailLoading && (
          <DialogActions sx={{ p: 2 }}>
            <Tooltip title={`Reply to ${selected.email}`}>
              <Button
                component="a"
                href={replyMailto}
                sx={{ color: theme.teal, fontWeight: 700, textTransform: "none" }}
              >
                <Reply size={16} style={{ marginRight: 6 }} />
                Reply via Email
              </Button>
            </Tooltip>
            <Button
              disabled={actionLoading}
              onClick={handleDelete}
              sx={{ color: theme.red, fontWeight: 700, textTransform: "none" }}
            >
              <Trash2 size={16} style={{ marginRight: 6 }} />
              Delete
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}
